import { InformationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import SelectComponent from "@via/components/select/select";
import { ApiConfigFormValues } from "@via/pages/projects/metadata/configuration/api-configs/utils/helper";
import MappingFieldsSection from "@via/pages/projects/metadata/configuration/api-configs/components/mapping-fields-section";
import { SelectOption } from "@via/utils/interfaces";
import { FC } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface ExtraGroupSectionProps {
  control: Control<ApiConfigFormValues>;
  index: number;
  readOnly: boolean;
  onRemove: (index: number) => void;
  collectionOptions: SelectOption[];
  fieldOptionsByCollectionId: Record<string, SelectOption[]>;
  joinFieldOptionsByCollectionId: Record<string, SelectOption[]>;
}

const ExtraGroupSection: FC<ExtraGroupSectionProps> = ({
  control,
  index,
  readOnly,
  onRemove,
  collectionOptions,
  fieldOptionsByCollectionId,
  joinFieldOptionsByCollectionId,
}) => {
  const groupCollectionId = useWatch({
    control,
    name: `groups.${index}.collectionID`,
  }) as string;
  const enableJoin = useWatch({
    control,
    name: `groups.${index}.enableJoin`,
  }) as boolean;
  const sourceCollectionId = useWatch({
    control,
    name: `groups.${index}.join.source_collection`,
  }) as string;
  const joinTooltipId = `tooltip-join-using-fields-${index}`;

  return (
    <div className="min-w-0 space-y-4 rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-sm font-medium leading-6 text-gray-900">
          Collection Group
        </label>
        {!readOnly && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="inline-flex shrink-0 rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500">
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      <SelectComponent
        control={control}
        name={`groups.${index}.collectionID`}
        options={collectionOptions}
        placeholder="Select Collection"
        enableClear={!readOnly}
        readOnly={readOnly}
        isSearchable
      />

      <div className="mt-2">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <Controller
            control={control}
            name={`groups.${index}.enableJoin`}
            render={({ field }) => (
              <input
                type="checkbox"
                checked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                className="h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:border-indigo-600 focus:ring-inset focus:ring-1 focus:ring-indigo-600 focus:ring-offset-0"
                disabled={readOnly}
              />
            )}
          />
          Custom Join Using Fields
          <InformationCircleIcon
            id={joinTooltipId}
            className="h-5 w-5 text-gray-900"
          />
        </label>
        <Tooltip anchorSelect={`#${joinTooltipId}`}>
          <div className="max-w-xs">
            Use this when the collection is not connected through the default
            relationship path. Match a field from a source collection to a field
            in this collection to bring in related data.
          </div>
        </Tooltip>
      </div>

      {enableJoin && (
        <div className="grid min-w-0 grid-cols-1 gap-3 rounded-md border border-gray-100 bg-gray-50 p-3 xl:grid-cols-3">
          <SelectComponent
            control={control}
            name={`groups.${index}.join.source_collection`}
            options={collectionOptions}
            placeholder="Source Collection"
            enableClear={!readOnly}
            readOnly={readOnly}
            isSearchable
          />
          <SelectComponent
            control={control}
            name={`groups.${index}.join.source_field`}
            options={joinFieldOptionsByCollectionId[sourceCollectionId] ?? []}
            placeholder="Source Field"
            enableClear={!readOnly}
            readOnly={readOnly}
            isSearchable
          />
          <SelectComponent
            control={control}
            name={`groups.${index}.join.foreign_field`}
            options={joinFieldOptionsByCollectionId[groupCollectionId] ?? []}
            placeholder="Target Field"
            enableClear={!readOnly}
            readOnly={readOnly}
            isSearchable
          />
        </div>
      )}

      <MappingFieldsSection
        control={control}
        name={`groups.${index}.fields`}
        fieldOptions={fieldOptionsByCollectionId[groupCollectionId] ?? []}
        readOnly={readOnly}
      />
    </div>
  );
};

export default ExtraGroupSection;
